import React, { useEffect, useState } from "react";
import {
    Accordion,
    Badge,
    Center,
    Group,
    Loader,
    Paper,
    Stack,
    Text,
    ThemeIcon,
} from "@mantine/core";
import {
    IconCar,
    IconCheck,
    IconParking,
} from "@tabler/icons-react";

import QRGenerator from "../../../components/QRGenerator";

type ParkingAreaDto = {
    parkingAreaId: number;
    name: string;
};

type ParkingValidationDto = {
    isValid: boolean;
    eventId: number;
    personId: number;
    personName?: string;
    eventName?: string;
    parkingAreas?: ParkingAreaDto[];
};

type Props = {
    show: boolean;
    webUrl?: string;
    eid: number;
    pid: number;
};

export default function VipParkingSection({
    show,
    webUrl,
    eid,
    pid,
}: Props) {
    const [loading, setLoading] = useState(false);
    const [areas, setAreas] = useState<ParkingAreaDto[]>([]);
    const [valid, setValid] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!show || !eid || !pid) {
            setAreas([]);
            setValid(false);
            return;
        }

        let cancelled = false;

        const loadParking = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/parking/validate/${eid}/${pid}`
                );

                if (!response.ok) {
                    throw new Error("Kunne ikke hente parkeringsadgang.");
                }

                const data: ParkingValidationDto =
                    await response.json();

                if (cancelled) return;

                setValid(data.isValid);
                setAreas(
                    data.isValid
                        ? data.parkingAreas ?? []
                        : []
                );
            } catch (err) {
                if (cancelled) return;

                setValid(false);
                setAreas([]);
                setError("Kunne ikke hente parkeringsområder.");
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadParking();

        return () => {
            cancelled = true;
        };
    }, [show, eid, pid]);

    if (!show) return null;

    const parkingUrl = `${(webUrl || window.location.origin).replace(/\/$/, "")
        }/parkering/${eid}/${pid}`;

    return (
        <Accordion.Item value="vip-parking">
            <Accordion.Control
                icon={
                    <ThemeIcon
                        color="red"
                        variant="light"
                        radius="xl"
                    >
                        <IconCar size={18} />
                    </ThemeIcon>
                }
            >
                <Group gap="xs">
                    <Text fw={600}>Parkering</Text>

                    {!loading && valid && areas.length > 0 && (
                        <Badge color="green" variant="light">
                            {areas.length}{" "}
                            {areas.length === 1
                                ? "område"
                                : "områder"}
                        </Badge>
                    )}
                </Group>
            </Accordion.Control>

            <Accordion.Panel>
                <Stack gap="md" align="center">
                    {loading ? (
                        <Center py="lg">
                            <Loader color="red" size="sm" />
                        </Center>
                    ) : error ? (
                        <Text c="red" size="sm" ta="center">
                            {error}
                        </Text>
                    ) : !valid ? (
                        <Text c="red" size="sm" ta="center">
                            Ingen aktiv parkeringsadgang.
                        </Text>
                    ) : (
                        <>
                            <Stack
                                gap="xs"
                                w="100%"
                            >
                                <Text fw={700} ta="center">
                                    Dine parkeringsområder
                                </Text>

                                {areas.map((area) => (
                                    <Paper
                                        key={area.parkingAreaId}
                                        withBorder
                                        radius="md"
                                        p="sm"
                                        w="100%"
                                    >
                                        <Group
                                            justify="space-between"
                                            wrap="nowrap"
                                        >
                                            <Group gap="sm">
                                                <ThemeIcon
                                                    color="green"
                                                    variant="light"
                                                    radius="xl"
                                                >
                                                    <IconParking
                                                        size={18}
                                                    />
                                                </ThemeIcon>

                                                <Text fw={600}>
                                                    {area.name}
                                                </Text>
                                            </Group>

                                            <IconCheck
                                                size={20}
                                                color="green"
                                            />
                                        </Group>
                                    </Paper>
                                ))}
                            </Stack>

                            <Paper
                                withBorder
                                radius="lg"
                                p="lg"
                                w="100%"
                            >
                                <Stack
                                    align="center"
                                    gap="sm"
                                >
                                    <Text fw={700} size="lg">
                                        Parkeringsadgang
                                    </Text>

                                    <QRGenerator
                                        value={parkingUrl}
                                    />

                                    <Text
                                        size="sm"
                                        c="dimmed"
                                        ta="center"
                                    >
                                        Vis QR-koden ved
                                        parkeringsindgangen
                                    </Text>
                                </Stack>
                            </Paper>

                            <Text
                                size="xs"
                                c="dimmed"
                                ta="center"
                            >
                                QR-koden gælder for alle
                                dine godkendte
                                parkeringsområder.
                            </Text>
                        </>
                    )}
                </Stack>
            </Accordion.Panel>
        </Accordion.Item>
    );
}